import { StatusBadge, STATUS_BADGES } from "@/shared/components/StatusBadge";
import type { OrgLlmConnectionRecord, OrgLlmModel } from "@/shared/domain/orgs";
import { useVerifyOrgLlmModel } from "@/shared/domain/orgs/hooks";
import { isRobotoManaged } from "@/shared/domain/orgs/orgModelConfig";
import { RobotoButton } from "@/shared/foundation/primitives";

import { verifiedWhen } from "../verification";
import { VerifyOutcome } from "../VerifyOutcome";

import {
  isEnabled,
  modelUnavailableReason,
  ownModelVerification,
} from "./modelList";
import styles from "./OrgAiModels.module.css";

/** An own-connection model's verification, with when it last worked or why it failed on hover. Nothing for a Roboto model. */
function ModelVerificationBadge({ model }: { model: OrgLlmModel }) {
  const verification = ownModelVerification(model);
  switch (verification?.state) {
    case "verified":
      return (
        <StatusBadge
          status={STATUS_BADGES.modelVerification.verified}
          tooltip={verifiedWhen(verification.at)}
        />
      );
    case "unverified":
      return (
        <StatusBadge status={STATUS_BADGES.modelVerification.not_verified} />
      );
    case "failed":
      return (
        <StatusBadge
          status={STATUS_BADGES.modelVerification.failed}
          tooltip={verification.error}
        />
      );
    default:
      return null;
  }
}

/**
 * A model's status cell: "Off" for a switched-off model; "Roboto-managed" for
 * a Roboto model; for an own model, its verification, and why the org's
 * own-connections flag, its tier or the model's connection keeps it from
 * running, if any does.
 */
export function ModelStatus({
  model,
  connections,
  isPremium,
  ownConnectionsEnabled,
}: {
  model: OrgLlmModel;
  connections: readonly OrgLlmConnectionRecord[];
  isPremium: boolean;
  ownConnectionsEnabled: boolean;
}) {
  if (!isEnabled(model)) {
    return <span className={styles.secondaryText}>Off</span>;
  }
  if (isRobotoManaged(model)) {
    return <span className={styles.secondaryText}>Roboto-managed</span>;
  }
  const unavailable = modelUnavailableReason(
    model,
    connections,
    isPremium,
    ownConnectionsEnabled,
  );
  return (
    <span className={styles.badgeStack}>
      <ModelVerificationBadge model={model} />
      {unavailable !== undefined && (
        <StatusBadge
          status={STATUS_BADGES.modelVerification.unavailable}
          tooltip={unavailable}
        />
      )}
    </span>
  );
}

/** A Verify button for one own-connection model on the saved list, and the outcome of the last click. */
export function VerifyModelAction({
  orgId,
  model,
}: {
  orgId: string;
  model: OrgLlmModel;
}) {
  const verifyMutation = useVerifyOrgLlmModel();
  return (
    <span className={styles.verifyAction}>
      <VerifyOutcome
        result={verifyMutation.data}
        error={verifyMutation.error}
      />
      <RobotoButton
        variant="ghost"
        size="sm"
        loading={verifyMutation.isPending}
        onClick={() => verifyMutation.mutate({ orgId, profileId: model.id })}
        aria-label={`Verify ${model.label}`}
      >
        Verify
      </RobotoButton>
    </span>
  );
}
